import { runAz, rethrowIfAzUnavailable } from "./az.js";
import { ACR_PULL_ROLE_ID } from "./rbac.js";

export type TagCheck = "present" | "absent" | "unknown";

/**
 * Best-effort check that <repo>:<tag> exists in the ACR. Returns "unknown"
 * if the az call fails (network/permission) so callers warn-and-proceed
 * rather than block on a flaky check — createVersion + poll-to-failed is
 * the real backstop.
 */
export async function imageTagExists(acrName: string, repo: string, tag: string): Promise<TagCheck> {
  try {
    const out = await runAz([
      "acr",
      "repository",
      "show-tags",
      "-n",
      acrName,
      "--repository",
      repo,
      "-o",
      "json",
    ]);
    const tags = JSON.parse(out) as string[];
    return tags.includes(tag) ? "present" : "absent";
  } catch {
    return "unknown";
  }
}

/**
 * Deterministic per-customer ACR name: the Foundry account name (already globally
 * unique — it's the custom subdomain) reduced to ACR's charset (alphanumeric,
 * 5-50) with an "acr" suffix. Both the provision pass (foundry create) and the
 * deploy heal pass derive the same name, so they resolve to one registry without
 * shared state.
 */
export function deriveAcrName(accountName: string): string {
  const base = accountName.replace(/[^a-z0-9]/gi, "").toLowerCase().slice(0, 47);
  return `${base}acr`.padEnd(5, "0");
}

export interface EnsureAcrResult {
  created: boolean;
  resourceId: string; // ARM id — the scope for the AcrPull grant
  loginServer: string; // <name>.azurecr.io
}

/**
 * Ensure a Basic ACR exists in the resource group (create-if-missing, idempotent),
 * with Microsoft Entra ARM-token auth enabled so the Foundry project MI can
 * authenticate the image pull. Mirrors foundry-create's show-then-create pattern.
 */
export async function ensureAcr(args: { acrName: string; resourceGroup: string; location: string }): Promise<EnsureAcrResult> {
  let existing: { id?: string; loginServer?: string } | null = null;
  try {
    existing = JSON.parse(
      await runAz(["acr", "show", "-n", args.acrName, "-g", args.resourceGroup, "-o", "json"]),
    ) as { id?: string; loginServer?: string };
  } catch (e) {
    rethrowIfAzUnavailable(e);
    existing = null; // absent — the idempotent create below is the backstop
  }
  if (existing?.id && existing.loginServer) {
    await ensureArmTokenAuth(args.acrName);
    return { created: false, resourceId: existing.id, loginServer: existing.loginServer };
  }
  const created = JSON.parse(
    await runAz(["acr", "create", "-n", args.acrName, "-g", args.resourceGroup, "--sku", "Basic", "-l", args.location, "-o", "json"]),
  ) as { id?: string; loginServer?: string };
  await ensureArmTokenAuth(args.acrName);
  return {
    created: true,
    resourceId: created.id ?? "",
    loginServer: created.loginServer ?? `${args.acrName}.azurecr.io`,
  };
}

/**
 * Best-effort: ensure the registry accepts Entra ARM tokens (required for the
 * project MI to authenticate the pull). New ACRs default to enabled; this
 * re-asserts it for a reused registry. The toggle is a preview az command —
 * swallow its absence rather than fail the deploy.
 */
async function ensureArmTokenAuth(acrName: string): Promise<void> {
  try {
    await runAz(["acr", "config", "authentication-as-arm", "update", "-r", acrName, "--status", "enabled"]);
  } catch (e) {
    rethrowIfAzUnavailable(e);
    // preview command unavailable / already enabled — proceed
  }
}

/**
 * Copy a public image into the ACR server-side (`az acr import`, no local build).
 * Idempotent — skips when the tag is already present, and tolerates an
 * "already exists" race when the pre-check was inconclusive.
 */
export async function ensureImageInAcr(args: { acrName: string; sourceRef: string; repo: string; tag: string }): Promise<{ imported: boolean }> {
  if ((await imageTagExists(args.acrName, args.repo, args.tag)) === "present") {
    return { imported: false };
  }
  try {
    await runAz(["acr", "import", "-n", args.acrName, "--source", args.sourceRef, "--image", `${args.repo}:${args.tag}`]);
  } catch (e) {
    if (e instanceof Error && /already exists/i.test(e.message)) return { imported: false };
    throw e;
  }
  return { imported: true };
}

/**
 * Grant AcrPull to a principal at the ACR resource scope via `az` (idempotent).
 * Used by the provision pass, which runs in the az-only foundry-create module
 * (no TokenCredential). The deploy heal pass uses rbac.ts/grantAcrPull instead.
 */
export async function grantAcrPullViaAz(args: { acrResourceId: string; principalId: string }): Promise<void> {
  try {
    await runAz([
      "role", "assignment", "create",
      "--assignee-object-id", args.principalId,
      "--assignee-principal-type", "ServicePrincipal",
      "--role", ACR_PULL_ROLE_ID,
      "--scope", args.acrResourceId,
    ]);
  } catch (e) {
    // az role assignment create emits RoleAssignmentExists on a duplicate; swallow
    // ONLY that (a broad /exists/i match would hide ResourceGroupNotFound / auth errors).
    if (e instanceof Error && /RoleAssignmentExists/i.test(e.message)) return;
    throw e;
  }
}
